import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Mail, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminContent() {
  const { getAuthHeaders } = useApp();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchContent = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/content`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/content`, {
        contact: data.contact,
        shipping: data.shipping,
        returns: data.returns,
      }, { headers: getAuthHeaders() });
      setData(res.data);
      toast.success('Site content saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <div className="p-6 text-muted-foreground">Loading...</div>;
  }

  const setContact = (field, value) => setData({ ...data, contact: { ...data.contact, [field]: value } });
  const setPage = (key, field, value) => setData({ ...data, [key]: { ...data[key], [field]: value } });

  return (
    <div className="p-6" data-testid="admin-content">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            Site Content
          </h1>
          <p className="text-muted-foreground mt-1">Manage your footer contact details and info pages</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full" data-testid="save-content-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact */}
        <div className="bg-white rounded-xl border border-border p-6 h-fit" data-testid="content-contact-card">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="h-5 w-5 text-[#EA580C]" />
            <h2 className="font-semibold">Contact &amp; Social</h2>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" value={data.contact.email} onChange={(e) => setContact('email', e.target.value)} className="mt-1" data-testid="contact-email-input" />
            </div>
            <div>
              <Label htmlFor="c-phone">Phone (leave blank to hide)</Label>
              <Input id="c-phone" value={data.contact.phone} onChange={(e) => setContact('phone', e.target.value)} className="mt-1" placeholder="+64 ..." data-testid="contact-phone-input" />
            </div>
            <div>
              <Label htmlFor="c-address">Address</Label>
              <Input id="c-address" value={data.contact.address} onChange={(e) => setContact('address', e.target.value)} className="mt-1" data-testid="contact-address-input" />
            </div>
            <div>
              <Label htmlFor="c-fb">Facebook URL</Label>
              <Input id="c-fb" value={data.contact.facebook} onChange={(e) => setContact('facebook', e.target.value)} className="mt-1" placeholder="https://facebook.com/..." data-testid="contact-facebook-input" />
            </div>
            <div>
              <Label htmlFor="c-ig">Instagram URL (leave blank to hide)</Label>
              <Input id="c-ig" value={data.contact.instagram} onChange={(e) => setContact('instagram', e.target.value)} className="mt-1" placeholder="https://instagram.com/..." data-testid="contact-instagram-input" />
            </div>
            <div>
              <Label htmlFor="c-tw">Twitter/X URL (leave blank to hide)</Label>
              <Input id="c-tw" value={data.contact.twitter} onChange={(e) => setContact('twitter', e.target.value)} className="mt-1" placeholder="https://x.com/..." data-testid="contact-twitter-input" />
            </div>
          </div>
        </div>

        {/* Info pages */}
        <div className="space-y-6">
          {['shipping', 'returns'].map((key) => (
            <div key={key} className="bg-white rounded-xl border border-border p-6" data-testid={`content-${key}-card`}>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-[#0B1120]" />
                <h2 className="font-semibold capitalize">{key === 'shipping' ? 'Shipping Info Page' : 'Returns & Exchanges Page'}</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor={`${key}-title`}>Page Title</Label>
                  <Input id={`${key}-title`} value={data[key].title} onChange={(e) => setPage(key, 'title', e.target.value)} className="mt-1" data-testid={`${key}-title-input`} />
                </div>
                <div>
                  <Label htmlFor={`${key}-content`}>Page Content</Label>
                  <Textarea id={`${key}-content`} value={data[key].content} onChange={(e) => setPage(key, 'content', e.target.value)} rows={12} className="mt-1 text-sm" data-testid={`${key}-content-input`} />
                  <p className="text-xs text-muted-foreground mt-1">Line breaks are preserved on the public page.</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
